import { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box, Button, TextField, Typography, Alert, CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password) { setError('Please fill in all fields'); return; }
    setLoading(true);
    try {
      await login(formData);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header bar */}
      <Box sx={{
        bgcolor: '#1a1a2e', color: '#f7f3ee',
        px: 5, py: 3.5,
        display: 'flex', alignItems: 'center', gap: 1.75,
        borderBottom: '3px solid #c9a84c',
      }}>
        <Box sx={{
          width: 42, height: 42, bgcolor: '#c9a84c', borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20,
        }}>🔐</Box>
        <Box>
          <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontSize: '1.2rem', lineHeight: 1.2 }}>
            Vaults Portal
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#f0e0b0', letterSpacing: '2px', textTransform: 'uppercase' }}>
            Secure Access
          </Typography>
        </Box>
      </Box>

      {/* Login card */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>Welcome back</Typography>
            <Typography variant="body2" color="text.secondary">Sign in to access your vault</Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}
            sx={{ bgcolor: 'background.paper', border: '1px solid #e2dbd0', borderRadius: 4, p: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField margin="normal" required fullWidth id="email" label="Email Address" name="email"
              autoComplete="email" autoFocus value={formData.email} onChange={handleChange} />
            <TextField margin="normal" required fullWidth name="password" label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password" autoComplete="current-password" value={formData.password} onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }} />

            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 1, py: 1.5 }}
              startIcon={loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <LockOpenIcon />}
              disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
